import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Inbox, SlidersHorizontal, Loader2, Mail, Calendar, MapPin,
  DollarSign, Check, Trash2, Plus, Save, Music, ExternalLink,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-zinc-950/85 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets-0z36b82j.emergentagent.net/job_web-forge-2354/artifacts/3681ae35dc49d13f_38913A32-E62B-45A1-A1AE-D8435497D960.png"
              alt="Zee Nation"
              className="h-11 w-auto object-contain mix-blend-lighten"
            />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-300">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block font-mono text-xs text-zinc-500">{user?.email}</span>
            <button onClick={doLogout} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-zinc-400 hover:text-rose-500 transition-colors" data-testid="logout-btn">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
        <div className="flex gap-2 mb-10 border-b border-zinc-800">
          <TabBtn active={tab === "enquiries"} onClick={() => setTab("enquiries")} icon={Inbox} label="Enquiries" testid="tab-enquiries" />
          <TabBtn active={tab === "content"} onClick={() => setTab("content")} icon={SlidersHorizontal} label="Site Content" testid="tab-content" />
        </div>

        {tab === "enquiries" ? <EnquiriesPanel /> : <ContentPanel />}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-xs uppercase tracking-wider transition-colors border-b-2 -mb-px ${
        active ? "border-rose-600 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"
      }`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

/* ---------------- Enquiries ---------------- */
function EnquiriesPanel() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/enquiries").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const toggleHandled = async (it) => {
    try {
      await api.patch(`/enquiries/${it.id}`, { handled: !it.handled });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, handled: !x.handled } : x)));
    } catch (e) {
      toast.error("Could not update");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/enquiries/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Enquiry deleted");
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  if (items === null) return <Spinner />;

  const filtered = items.filter((x) => filter === "all" || (filter === "pending" ? !x.handled : x.handled));
  const pendingCount = items.filter((x) => !x.handled).length;

  return (
    <div data-testid="enquiries-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-anton uppercase text-3xl text-white leading-none">Booking Enquiries</h2>
          <p className="mt-2 font-mono text-xs text-zinc-500">{items.length} total · {pendingCount} pending</p>
        </div>
        <div className="flex gap-2">
          {["all", "pending", "handled"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`font-mono text-[11px] uppercase tracking-wider px-4 py-2 rounded-sm border transition-colors ${
                filter === f ? "border-rose-600 text-white bg-rose-600/10" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"
              }`}
              data-testid={`filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="py-20 text-center border border-dashed border-zinc-800 rounded-md" data-testid="enquiries-empty">
          <Inbox className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
          <p className="font-mono text-sm text-zinc-500">No enquiries here yet.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filtered.map((it, i) => (
            <div key={it.id} className="bg-[#121216] border border-zinc-800 rounded-md p-6" data-testid={`enquiry-card-${i}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display text-2xl uppercase text-white leading-none">{it.name}</h3>
                    <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm ${it.handled ? "bg-emerald-600/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
                      {it.handled ? "Handled" : "Pending"}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm bg-rose-600/15 text-rose-400">{it.eventType}</span>
                  </div>
                  <div className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-zinc-400">
                    <Detail icon={Mail} value={<a href={`mailto:${it.email}`} className="hover:text-rose-500 break-all">{it.email}</a>} />
                    <Detail icon={Calendar} value={it.eventDate} />
                    <Detail icon={MapPin} value={it.cityVenue} />
                    {it.budget ? <Detail icon={DollarSign} value={it.budget} /> : null}
                  </div>
                  <p className="mt-4 text-sm text-zinc-300 leading-relaxed max-w-2xl whitespace-pre-wrap">{it.message}</p>
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-zinc-600">
                    {new Date(it.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleHandled(it)}
                    className={`inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider px-4 py-2 rounded-sm border transition-colors ${it.handled ? "border-zinc-700 text-zinc-400 hover:text-white" : "border-emerald-600/50 text-emerald-400 hover:bg-emerald-600/10"}`}
                    data-testid={`enquiry-handled-${i}`}
                  >
                    <Check className="w-3.5 h-3.5" /> {it.handled ? "Mark pending" : "Mark handled"}
                  </button>
                  <button
                    onClick={() => remove(it.id)}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-sm border border-zinc-800 text-zinc-500 hover:text-rose-500 hover:border-rose-600/50 transition-colors"
                    data-testid={`enquiry-delete-${i}`}
                    aria-label="Delete enquiry"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Detail({ icon: Icon, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-zinc-600 shrink-0" />
      <span>{value}</span>
    </div>
  );
}

/* ---------------- Content editor ---------------- */
function ContentPanel() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/content").then((r) => setContent(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setContent((c) => ({ ...c, [k]: v }));
  const setTrack = (i, k, v) => setContent((c) => ({ ...c, tracks: c.tracks.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)) }));
  const addTrack = () => setContent((c) => ({ ...c, tracks: [...c.tracks, { id: crypto.randomUUID(), title: "NEW TRACK", artist: "Mr Zee", genre: "Hip-Hop/Rap", audiomack_url: "", spotify_url: "", apple_url: "", audio_preview: "" }] }));
  const removeTrack = (i) => setContent((c) => ({ ...c, tracks: c.tracks.filter((_, idx) => idx !== i) }));
  const setStat = (i, k, v) => setContent((c) => ({ ...c, stats: c.stats.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", content);
      toast.success("Site content updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!content) return <Spinner />;

  return (
    <div className="space-y-12 max-w-4xl" data-testid="content-panel">
      <div className="flex items-center justify-between sticky top-16 z-30 bg-[#09090b] py-3">
        <h2 className="font-anton uppercase text-3xl text-white leading-none">Site Content</h2>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white font-mono text-xs uppercase tracking-wider px-6 py-3 rounded-sm transition-colors" data-testid="content-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save changes</>}
        </button>
      </div>

      <Group title="Hero">
        <Row label="Badge"><Inp value={content.hero_badge} onChange={(v) => set("hero_badge", v)} testid="edit-hero-badge" /></Row>
        <Row label="Headline"><Inp value={content.hero_headline} onChange={(v) => set("hero_headline", v)} testid="edit-hero-headline" /></Row>
        <Row label="Tagline"><Area value={content.hero_tagline} onChange={(v) => set("hero_tagline", v)} testid="edit-hero-tagline" /></Row>
      </Group>

      <Group title="Music" action={<AddBtn onClick={addTrack} label="Add track" testid="add-track-btn" />}>
        <Row label="Section subtext"><Area value={content.music_subtext} onChange={(v) => set("music_subtext", v)} testid="edit-music-subtext" /></Row>
        {content.tracks.map((t, i) => (
          <div key={t.id} className="bg-[#0c0c0f] border border-zinc-800 rounded-md p-5 space-y-3" data-testid={`edit-track-${i}`}>
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-rose-500"><Music className="w-3.5 h-3.5" /> Track {i + 1}</span>
              <button onClick={() => removeTrack(i)} className="text-zinc-500 hover:text-rose-500 transition-colors" data-testid={`remove-track-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Inp label="Title" value={t.title} onChange={(v) => setTrack(i, "title", v)} testid={`track-title-input-${i}`} />
              <Inp label="Artist / credit" value={t.artist} onChange={(v) => setTrack(i, "artist", v)} testid={`track-artist-input-${i}`} />
              <Inp label="Genre / project" value={t.genre} onChange={(v) => setTrack(i, "genre", v)} testid={`track-genre-input-${i}`} />
              <Inp label="Audio preview URL" value={t.audio_preview} onChange={(v) => setTrack(i, "audio_preview", v)} testid={`track-preview-input-${i}`} />
              <Inp label="Audiomack URL" value={t.audiomack_url} onChange={(v) => setTrack(i, "audiomack_url", v)} testid={`track-audiomack-input-${i}`} />
              <Inp label="Spotify URL" value={t.spotify_url} onChange={(v) => setTrack(i, "spotify_url", v)} testid={`track-spotify-input-${i}`} />
              <Inp label="Apple Music URL" value={t.apple_url} onChange={(v) => setTrack(i, "apple_url", v)} testid={`track-apple-input-${i}`} />
            </div>
          </div>
        ))}
      </Group>

      <Group title="Story / About">
        <Row label="Tag"><Inp value={content.story_tag} onChange={(v) => set("story_tag", v)} testid="edit-story-tag" /></Row>
        <Row label="Headline"><Inp value={content.story_headline} onChange={(v) => set("story_headline", v)} testid="edit-story-headline" /></Row>
        <Row label="Body"><Area value={content.story_body} onChange={(v) => set("story_body", v)} testid="edit-story-body" /></Row>
        <Row label="Stats">
          <div className="grid sm:grid-cols-3 gap-3">
            {content.stats.map((s, i) => (
              <div key={i} className="bg-[#0c0c0f] border border-zinc-800 rounded-md p-4 space-y-2" data-testid={`edit-stat-${i}`}>
                <Inp label="Value" value={s.value} onChange={(v) => setStat(i, "value", v)} testid={`stat-value-${i}`} />
                <Inp label="Label" value={s.label} onChange={(v) => setStat(i, "label", v)} testid={`stat-label-${i}`} />
                <Inp label="Sub" value={s.sub} onChange={(v) => setStat(i, "sub", v)} testid={`stat-sub-${i}`} />
              </div>
            ))}
          </div>
        </Row>
      </Group>

      <Group title="Booking">
        <Row label="Subtext"><Area value={content.booking_subtext} onChange={(v) => set("booking_subtext", v)} testid="edit-booking-subtext" /></Row>
        <Row label="Booking email"><Inp value={content.booking_email} onChange={(v) => set("booking_email", v)} testid="edit-booking-email" /></Row>
      </Group>

      <div className="flex justify-end pb-10">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white font-mono text-xs uppercase tracking-wider px-6 py-3 rounded-sm transition-colors" data-testid="content-save-btn-bottom">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save changes</>}
        </button>
      </div>
    </div>
  );
}

function Group({ title, action, children }) {
  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
        <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{title}</h3>
        {action}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid sm:grid-cols-[160px_1fr] gap-2 sm:gap-6 items-start">
      <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 pt-2">{label}</span>
      <div>{children}</div>
    </div>
  );
}

const baseInput = "w-full bg-[#0c0c0f] border border-zinc-800 focus:border-rose-600 rounded-sm px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors";

function Inp({ label, value, onChange, testid }) {
  return (
    <label className="block">
      {label && <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-600 mb-1 block">{label}</span>}
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={baseInput} data-testid={testid} />
    </label>
  );
}

function Area({ value, onChange, testid }) {
  return <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${baseInput} resize-none`} data-testid={testid} />;
}

function AddBtn({ onClick, label, testid }) {
  return (
    <button onClick={onClick} className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-rose-500 hover:text-rose-400 transition-colors" data-testid={testid}>
      <Plus className="w-4 h-4" /> {label}
    </button>
  );
}

function Spinner() {
  return (
    <div className="py-24 flex justify-center">
      <Loader2 className="w-7 h-7 text-rose-600 animate-spin" />
    </div>
  );
}
